import { useMemo } from 'react';
import { supabaseCoreClient } from '../supabase.ts';
import { useQuery } from "@supabase-cache-helpers/postgrest-react-query";
import { commonQueryProps } from './cache.js';
import { useUIComponents } from './ui-components.js';
import { useTables } from './tables.js';
import type { 
  RouteInfo, 
  RouteMetadata, 
  UIComponentMetadata, 
  ProcessedTableMetadata,
  EnrichedRouteInfo 
} from '../../types/metadata.js';
import { persistedRelations, useMetadataBootReady } from '../persistentMetadataCache.js';

/**
 * Builds the Supabase query for fetching route metadata
 * @returns PostgrestBuilder query for table_route with necessary columns
 */
export const buildQueryForRoutes = () => {
  return supabaseCoreClient.from('table_route').select('route,table_info,id');
};

// Hook options interface
interface UseRoutesOptions {
  enabled?: boolean;
}

// Hook result interface
interface UseRoutesResult {
  data: RouteMetadata | null;
  error: any;
  isLoading: boolean;
}

/**
 * React hook to fetch route data with enrichment from UI components and tables
 * @param options - Options including enabled flag
 * @returns Route data with loading and error states
 */
export const useRoutes = ({ enabled }: UseRoutesOptions): UseRoutesResult => {
  const bootReady = useMetadataBootReady();
  // UI components:
  const { data: uiComponents, error: uiComponentError, isLoading: uiComponentIsLoading } = useUIComponents({ enabled });
  const { data: tableData, error: tableError, isLoading: tableIsLoading } = useTables({ enabled });
  const { data: routedata, error: routeError, isLoading: routeIsLoading } = useQuery(
    buildQueryForRoutes(),
    {
      ...commonQueryProps,
      enabled: enabled && bootReady,
      meta: persistedRelations('core_crm.table_route')
    }
  );

  // Memoized on the source query results so the processed object keeps a stable
  // identity across renders — consumers memoize off it.
  const processed = useMemo(() => {
    if (!routedata || !tableData || !uiComponents) return null;

    // TODO: put this in postProcessRoutes
    const routeDataEnriched: EnrichedRouteInfo[] = routedata.flatMap((route: RouteInfo) => {
      const tableInfo = tableData?.byId[route.table_info];
      if (!tableInfo) return [];
      return [{
        ...route,
        id: route.id,
        icon: 'document',
        table_name: tableInfo.table_name,
        label: tableInfo.ui_label,
        route_name: route.route,
        show_in_app_sidebar: tableInfo.ui_data?.show_in_app_sidebar ?? true,
        show_in_admin_sidebar: tableInfo.ui_data?.show_in_admin_sidebar ?? true,
      }];
    });

    return postProcessRoutes({
      routeData: routeDataEnriched,
      uiComponents,
      tableData
    });
  }, [routedata, tableData, uiComponents]);

  if (uiComponentIsLoading || tableIsLoading || routeIsLoading) {
    return {
      data: null,
      error: null,
      isLoading: true
    };
  }

  if (uiComponentError || tableError || routeError || !routedata || !tableData) {
    return {
      data: null,
      error: uiComponentError || tableError || routeError,
      isLoading: false
    };
  }

  return { data: processed, error: null, isLoading: false };
};

// Interface for postProcessRoutes parameters
interface PostProcessRoutesParams {
  routeData: EnrichedRouteInfo[];
  uiComponents: UIComponentMetadata[];
  tableData: ProcessedTableMetadata;
}

/**
 * Post-processes route data by enriching with UI components and creating lookup structures
 * @param params - Route data, UI components, and table data for enrichment
 * @returns Processed route metadata with component lookups
 */
export const postProcessRoutes = ({ routeData, uiComponents, tableData }: PostProcessRoutesParams): RouteMetadata => {
  if (!routeData) {
    return routeData as any;
  }

  // Enrich UI components with schema_name from table metadata
  const enrichedUiComponents = uiComponents.map(uiComponent => {
    const tableInfo = tableData?.byName?.[uiComponent.table_name];
    if (tableInfo?.schema_name) {
      return {
        ...uiComponent,
        schema_name: tableInfo.schema_name,
        ui_data: {
          ...uiComponent.ui_data,
          schema_name: tableInfo.schema_name
        }
      };
    }
    return uiComponent;
  });

  const routeDataEnriched: RouteMetadata = {
    asList: routeData,
    primaryUiComponentByTableName: {},
    primaryUiComponentByRouteName: {},
    tableToRouteName: {},
  };

  const primaryComponentsByTable: Record<string, UIComponentMetadata[]> = {};
  enrichedUiComponents.forEach((uiComponent) => {
    const { table_name, is_primary } = uiComponent;
    if (is_primary) {
      if (!primaryComponentsByTable[table_name]) {
        primaryComponentsByTable[table_name] = [];
      }
      primaryComponentsByTable[table_name].push(uiComponent);
    }
  });

  routeData.forEach((route) => {
    const { table_name } = route;
    if (table_name && route.route_name) {
      routeDataEnriched.tableToRouteName[table_name] = route.route_name;
      routeDataEnriched.primaryUiComponentByTableName[table_name] = {
        list: null,
        detail: null,
        search: null,
      };
      routeDataEnriched.primaryUiComponentByRouteName[route.route_name] = {
        list: null,
        detail: null,
        search: null,
      };
      
      if (primaryComponentsByTable[table_name]) {
        const components = primaryComponentsByTable[table_name];
        const listComponent = components.find((c) => c.component_type === 'List') || null;
        const detailComponent = components.find((c) => c.component_type === 'Detail') || null;
        const searchComponent = components.find((c) => c.component_type === 'Search') || null;
        
        routeDataEnriched.primaryUiComponentByTableName[table_name].list = listComponent;
        routeDataEnriched.primaryUiComponentByTableName[table_name].detail = detailComponent;
        routeDataEnriched.primaryUiComponentByTableName[table_name].search = searchComponent;
        routeDataEnriched.primaryUiComponentByRouteName[route.route_name].list = listComponent;
        routeDataEnriched.primaryUiComponentByRouteName[route.route_name].detail = detailComponent;
        routeDataEnriched.primaryUiComponentByRouteName[route.route_name].search = searchComponent;
      }
    }
  });

  return routeDataEnriched;
};
